import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { useMemo, useState } from 'react';
import colors from 'assets/scss/colors.module.scss';
import { TimeSeriesChart, ITimeSeriesChartConfig } from 'app/components/common/charts/TimeSeriesChart';
import { RadioButtonList, IRadioButtonListItem } from 'app/components/common/RadioButtonList';

interface IAkumaHistoryChart {
	agtId?: number;
	agtIds?: number[];
}

interface AkumaPlanerChartData {
	kw: string;
	kwRaw: number;
	wochen: number;
	gpWochen: number;
	gesamt: number;
	zu: number;
	uebertragungsQuote: number;
}

interface AkumaPrimeChartData {
	kw: string;
	kwRaw: number;
	einordnungTotal: number;
	kundenkontaktKunden: number;
	kundenkontaktquote: number;
}

type AkumaType = 'planer' | 'prime';

export const AkumaHistoryChart = observer((props: IAkumaHistoryChart) => {
	const { agtAkumaPlanerUiStore, agtAkumaPrimeUiStore } = useStore();
	const { agtId, agtIds } = props;
	const [akumaType, setAkumaType] = useState<AkumaType>('planer');

	// Toggle buttons configuration
	const toggleButtons: IRadioButtonListItem[] = [
		{ label: 'AKUMA Planer', value: 'planer' },
		{ label: 'AKUMA Prime', value: 'prime' },
	];

	// Build Planer chart configuration
	const planerChartConfig: ITimeSeriesChartConfig<AkumaPlanerChartData> = useMemo(
		() => ({
			title: 'AKUMA Planer Entwicklung (letzte 52 Wochen)',
			tag: 'AKUMA Planer',
			tagColor: 'yellow',
			height: 400,

			xAxisField: 'kw',
			xAxisRotation: 45,
			xAxisLabelInterval: 3,

			// Define Y-axes
			yAxes: [
				{
					name: 'Anzahl Kunden',
					position: 'left',
				},
				{
					name: 'Übertragungsquote (%)',
					position: 'right',
					max: 100,
					formatter: (v) => `${v}%`,
					valueType: 'percentage',
					splitLine: false,
				},
			],

			// Define derived fields (calculated at render time)
			derivedFields: [
				{
					fieldName: 'nichtUebertragen',
					calculate: (d) => d.gesamt - d.zu,
				},
				{
					fieldName: 'benchmark',
					calculate: () => 50, // Benchmark constant at 50%
				},
			],

			// Define series
			series: [
				{
					name: 'Kunden übertragen',
					dataField: 'zu',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.yellow,
					stack: 'kunden',
					barMaxWidth: 40,
				},
				{
					name: 'Kunden gesamt',
					dataField: 'nichtUebertragen',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.greyLight,
					stack: 'kunden',
					barMaxWidth: 40,
				},
				{
					name: 'Übertragungsquote (%)',
					dataField: 'uebertragungsQuote',
					yAxisIndex: 1,
					chartType: 'line',
					smooth: true,
					color: colors.orange,
					symbolSize: 8,
					lineStyle: { width: 3 },
				},
				{
					name: 'Benchmark (50%)',
					dataField: 'benchmark',
					yAxisIndex: 1,
					chartType: 'line',
					color: colors.red,
					lineStyle: { type: 'dashed', width: 2 },
					symbolSize: 0, // No symbols on benchmark line
				},
			],

			// Data loader function
			dataLoader: async (ids) => {
				return agtAkumaPlanerUiStore.loadAkumaHistoryLast52Weeks(ids);
			},
		}),
		[agtAkumaPlanerUiStore],
	);

	// Build Prime chart configuration
	const primeChartConfig: ITimeSeriesChartConfig<AkumaPrimeChartData> = useMemo(
		() => ({
			title: 'AKUMA Prime Entwicklung (letzte 52 Wochen)',
			tag: 'AKUMA Prime',
			tagColor: 'yellow',
			height: 400,

			xAxisField: 'kw',
			xAxisRotation: 45,
			xAxisLabelInterval: 3,

			// Define Y-axes
			yAxes: [
				{
					name: 'Anzahl Kunden',
					position: 'left',
				},
				{
					name: 'Kundenkontaktquote (%)',
					position: 'right',
					max: 100,
					formatter: (v) => `${v}%`,
					valueType: 'percentage',
					splitLine: false,
				},
			],

			// Define derived fields (calculated at render time)
			derivedFields: [
				{
					fieldName: 'sonstigeKunden',
					calculate: (d) => d.einordnungTotal - d.kundenkontaktKunden,
				},
			],

			// Define series
			series: [
				{
					name: 'Kundenkontakt-Kunden',
					dataField: 'kundenkontaktKunden',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.yellow,
					stack: 'kunden',
					barMaxWidth: 40,
				},
				{
					name: 'Sonstige Kunden',
					dataField: 'sonstigeKunden',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.greyLight,
					stack: 'kunden',
					barMaxWidth: 40,
				},
				{
					name: 'Kundenkontaktquote (%)',
					dataField: 'kundenkontaktquote',
					yAxisIndex: 1,
					chartType: 'line',
					smooth: true,
					color: colors.orange,
					symbolSize: 8,
					lineStyle: { width: 3 },
				},
			],

			// Data loader function
			dataLoader: async (ids) => {
				return agtAkumaPrimeUiStore.loadAkumaPrimeHistoryLast52Weeks(ids);
			},
		}),
		[agtAkumaPrimeUiStore],
	);

	return (
		<div>
			<div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
				<RadioButtonList value={akumaType} buttons={toggleButtons} onSelect={(value) => setAkumaType(value as AkumaType)} />
			</div>
			{akumaType === 'planer' ? (
				<TimeSeriesChart config={planerChartConfig} agtId={agtId} agtIds={agtIds} />
			) : (
				<TimeSeriesChart config={primeChartConfig} agtId={agtId} agtIds={agtIds} />
			)}
		</div>
	);
});
